import { useEffect, useMemo, useRef, useState } from 'react'

import { Actions, Entities } from '@space-lib/datahub'
import { SurvicateUtils } from '@space-lib/utils/survicate'
import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'
import { useGoogleAnalytics } from '@space-shell/common/hooks/useGoogleAnalytics'
import GlobalSearchService from '@space-shell/common/services/globalSearch/service'
import type { Result } from '@space-shell/common/services/globalSearch/types'
import { useMutation } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import { MIN_QUERY_VALUE } from './constants'
import { useNoSearchResults } from './hooks/useNoSearchResults'
import SearchBox from './searchBox'
import SearchResult from './searchResult'
import type { ResultsModalProps, PathParams } from './types'
import useSearchResults from './useSearchResults'

const GlobalSearch = () => {
  const [term, setTerm] = useState<string>('')
  const [enableSearch, setEnableSearch] = useState(false)
  const searchResultsRef = useRef<ResultsModalProps>(null)
  const { membership, lang } = useParams() as PathParams
  const { sendDataHubEvent } = useDataHubEvents()
  const { sendGoogleAnalyticsEvent } = useGoogleAnalytics()
  const handleNoSearchResults = useNoSearchResults({ lang, membership })

  const { mutate } = useMutation((membershipSlug: string) =>
    GlobalSearchService.putGlobalHierarchy(membershipSlug)
  )

  const { isLoading, isFetchingNextPage, data, isError, fetchNextPage, refetch, hasNextPage } =
    useSearchResults({ membership, enableSearch, term })

  const isLoadingResults = useMemo(() => {
    return enableSearch ? isLoading : false
  }, [isLoading, enableSearch])

  const results = useMemo(() => {
    return data?.pages?.flatMap((page) => page.results)
  }, [data])

  const isValidSearchResults =
    !isError && enableSearch && !isLoadingResults && Array.isArray(results) && results?.length > 0

  const isValidSearch = enableSearch && !isLoadingResults && !isError

  useEffect(() => {
    if (isValidSearchResults) {
      searchResultsRef.current?.open()

      sendDataHubEvent({
        group: Entities.MODAL_SEARCH,
        name: Actions.VIEW_SEARCH_RESULT,
        params: {
          search: {
            term,
            quantity: results.length
          }
        }
      })
      sendGoogleAnalyticsEvent({
        group: Entities.MODAL_SEARCH,
        name: Actions.VIEW_SEARCH_RESULT,
        label: term
      })

      SurvicateUtils.setVisitorTraits({ search: 'RESULTS' })
    }

    if (isValidSearch) {
      sendDataHubEvent({
        group: Entities.HEADER_SEARCH,
        name: Actions.CLICK_EXECUTE_SEARCH,
        params: {
          search: {
            term,
            quantity: results?.length
          }
        }
      })

      sendGoogleAnalyticsEvent({
        group: Entities.HEADER_SEARCH,
        name: Actions.CLICK_EXECUTE_SEARCH,
        label: term
      })
    }
  }, [
    isError,
    results,
    sendDataHubEvent,
    sendGoogleAnalyticsEvent,
    term,
    isLoadingResults,
    enableSearch,
    isValidSearchResults,
    isValidSearch
  ])

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (term.length > MIN_QUERY_VALUE) {
      setEnableSearch(true)
    }
  }

  const handleFetchNextPage = () => {
    const searchAfter = results?.slice(-1)?.[0]?.searchAfter.toString()

    fetchNextPage({ pageParam: { term, searchAfter } })
  }

  const handleCloseModal = () => {
    SurvicateUtils.resetVisitor()
    setEnableSearch(false)
    setTerm('')
  }

  useEffect(() => {
    mutate(membership)
  }, [membership, mutate])

  useEffect(() => {
    if (isError && !isLoading && !Array.isArray(results)) {
      searchResultsRef.current?.open()

      return
    }

    const isEmptySearchResults =
      !isError && Array.isArray(results) && results.length === 0 && enableSearch

    if (isEmptySearchResults) {
      sendDataHubEvent({
        group: Entities.HEADER_SEARCH,
        name: Actions.SEARCH_EMPTY,
        params: {
          search: {
            term
          }
        }
      })
      sendGoogleAnalyticsEvent({
        group: Entities.HEADER_SEARCH,
        name: Actions.SEARCH_EMPTY,
        label: term
      })

      handleCloseModal()
      handleNoSearchResults?.(term)

      return
    }
  }, [
    enableSearch,
    handleNoSearchResults,
    isError,
    isLoading,
    results,
    sendDataHubEvent,
    sendGoogleAnalyticsEvent,
    term
  ])

  return (
    <>
      <SearchBox
        onSubmit={handleSubmit}
        isLoading={isLoadingResults}
        term={term}
        onChange={setTerm}
      />
      <SearchResult
        ref={searchResultsRef}
        isError={isError}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        results={results as Result[]}
        term={term}
        hasNextPage={hasNextPage}
        onTryAgain={refetch}
        onLoadNextPage={handleFetchNextPage}
        onCloseModal={handleCloseModal}
      />
    </>
  )
}

export default GlobalSearch
